import { useState } from 'react';
import { NulaLogo } from '@/components/nula-logo/nula-logo';
import loginBackground from '@/assets/login/login-background.avif';
import styles from './login-illustration.module.css';

const STAR_COUNT = 4;

/**
 * Build randomized shooting-star descriptors. Each star gets a random launch
 * point across the upper band, a random delay so they never fire in sync, and a
 * random duration for varied streak speed. Only 4 stars total, with delays
 * spread across a long window — each is visibly bright for just ~2s of its
 * cycle, so at most ~3 overlap and usually only 1-2 are on screen at once.
 * Randomized once per mount — fresh every time the login screen appears. CSS
 * owns the motion; these values feed it via custom properties (see .star).
 */
function makeStars(count: number) {
  // Give each star its own horizontal slot (with jitter inside it) so launch
  // points spread evenly across the band instead of clustering by chance. The
  // band starts off-screen top-left (negative top/left) and runs to ~70%, so
  // stars visibly sweep IN from the left/top-left corner — the streak is born at
  // the edge rather than appearing mid-panel — and travel down-right from there.
  const START = -15; // % — off-screen left so the streak enters from the edge
  const SPAN = 85; // % — band width (-15% → 70%)
  const slot = SPAN / count; // % width of each star's slot
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    top: -10 + Math.random() * 32, // % — from just off the top edge into the upper band
    left: START + i * slot + Math.random() * slot, // % — even spread, enters from the left edge
    delay: Math.random() * 14, // s — spread launches wide to keep ≤3-4 visible at once
    duration: 5 + Math.random() * 3.5, // s — varied streak speed
  }));
}

/**
 * The right-side panel of the login screen — a rounded box with a calm
 * background photo (tinted with the brand gradient), sparse shooting stars
 * streaking behind the centered Nula enso mark, and concentric ripples
 * breathing outward. Purely decorative.
 */
export function LoginIllustration() {
  // useState initializer runs once per mount → stable random positions for the
  // life of the screen (no reshuffle on re-render).
  const [stars] = useState(() => makeStars(STAR_COUNT));

  return (
    <div
      className={styles.illo}
      data-tauri-drag-region
      aria-hidden
      style={{ '--login-photo': `url(${loginBackground})` } as React.CSSProperties}
    >
      <div className={styles.shootingStars}>
        {stars.map((s) => (
          <span
            key={s.id}
            className={styles.star}
            data-testid="shooting-star"
            style={
              {
                '--star-top': `${s.top}%`,
                '--star-left': `${s.left}%`,
                '--star-delay': `${s.delay}s`,
                '--star-duration': `${s.duration}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
      <div className={styles.ripples}>
        <span className={styles.rip} />
        <span className={styles.rip} />
        <span className={styles.rip} />
        <span className={styles.rip} />
        <span className={styles.mark}>
          <NulaLogo size={120} color="#ffffff" />
        </span>
      </div>
      <p className={styles.tagline}>Quietly intelligent.</p>
    </div>
  );
}
